import {
  type CreatePackageBatchRequest,
  type CreatePackageDayRequest,
  type CreatePackageFaqRequest,
  type CreatePackageFlightRequest,
  type CreatePackageHotelRequest,
  type CreatePackageImageRequest,
  type CreatePackageRequest,
  type CreatePackageVariantRequest,
  type PackageBatchOut,
  PackageBatchOutSchema,
  type PackageCollectionOut,
  PackageCollectionOutSchema,
  type PackageDayOut,
  PackageDayOutSchema,
  type PackageDetail,
  PackageDetailSchema,
  type PackageExclusionOut,
  PackageExclusionOutSchema,
  type PackageFaqOut,
  PackageFaqOutSchema,
  type PackageFlightOut,
  PackageFlightOutSchema,
  type PackageHeroInclusionOut,
  PackageHeroInclusionOutSchema,
  type PackageHotelOut,
  PackageHotelOutSchema,
  type PackageImageOut,
  PackageImageOutSchema,
  type PackageInclusionOut,
  PackageInclusionOutSchema,
  type PackageListQuery,
  type PackageListResponse,
  PackageListResponseSchema,
  type SetPackageCollectionsRequest,
  type SetPackageExclusionsRequest,
  type SetPackageHeroInclusionsRequest,
  type SetPackageInclusionsRequest,
  type UpdatePackageDayRequest,
  type UpdatePackageBatchRequest,
  type UpdatePackageFaqRequest,
  type UpdatePackageFlightRequest,
  type UpdatePackageHotelRequest,
  type UpdatePackageImageRequest,
  type UpdatePackageRequest,
  type UpdatePackageVariantRequest,
  type PackageVariantOut,
  PackageVariantOutSchema,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/** Package master API. Every response validated against the shared zod schema. */

export const listPackages = async (query: PackageListQuery): Promise<PackageListResponse> => {
  const { data } = await apiClient.get<unknown>('/packages', { params: query });
  return PackageListResponseSchema.parse(data);
};

export const getPackage = async (id: string): Promise<PackageDetail> => {
  const { data } = await apiClient.get<unknown>(`/packages/${id}`);
  return PackageDetailSchema.parse(data);
};

export const createPackage = async (payload: CreatePackageRequest): Promise<PackageDetail> => {
  const { data } = await apiClient.post<unknown>('/packages', payload);
  return PackageDetailSchema.parse(data);
};

export const updatePackage = async (
  id: string,
  payload: UpdatePackageRequest,
): Promise<PackageDetail> => {
  const { data } = await apiClient.patch<unknown>(`/packages/${id}`, payload);
  return PackageDetailSchema.parse(data);
};

export const deletePackage = async (id: string): Promise<void> => {
  await apiClient.delete(`/packages/${id}`);
};

// ── Days ──────────────────────────────────────────────────────────────────────

export const createPackageDay = async (
  packageId: string,
  payload: CreatePackageDayRequest,
): Promise<PackageDayOut> => {
  const { data } = await apiClient.post<unknown>(`/packages/${packageId}/days`, payload);
  return PackageDayOutSchema.parse(data);
};

export const updatePackageDay = async (
  packageId: string,
  childId: string,
  payload: UpdatePackageDayRequest,
): Promise<PackageDayOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/days/${childId}`,
    payload,
  );
  return PackageDayOutSchema.parse(data);
};

export const deletePackageDay = async (packageId: string, childId: string): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/days/${childId}`);
};

// ── Hotels ────────────────────────────────────────────────────────────────────

export const createPackageHotel = async (
  packageId: string,
  payload: CreatePackageHotelRequest,
): Promise<PackageHotelOut> => {
  const { data } = await apiClient.post<unknown>(`/packages/${packageId}/hotels`, payload);
  return PackageHotelOutSchema.parse(data);
};

export const updatePackageHotel = async (
  packageId: string,
  childId: string,
  payload: UpdatePackageHotelRequest,
): Promise<PackageHotelOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/hotels/${childId}`,
    payload,
  );
  return PackageHotelOutSchema.parse(data);
};

export const deletePackageHotel = async (packageId: string, childId: string): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/hotels/${childId}`);
};

// ── Flights ───────────────────────────────────────────────────────────────────

export const createPackageFlight = async (
  packageId: string,
  payload: CreatePackageFlightRequest,
): Promise<PackageFlightOut> => {
  const { data } = await apiClient.post<unknown>(`/packages/${packageId}/flights`, payload);
  return PackageFlightOutSchema.parse(data);
};

export const updatePackageFlight = async (
  packageId: string,
  childId: string,
  payload: UpdatePackageFlightRequest,
): Promise<PackageFlightOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/flights/${childId}`,
    payload,
  );
  return PackageFlightOutSchema.parse(data);
};

export const deletePackageFlight = async (packageId: string, childId: string): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/flights/${childId}`);
};

// ── FAQs ──────────────────────────────────────────────────────────────────────

export const createPackageFaq = async (
  packageId: string,
  payload: CreatePackageFaqRequest,
): Promise<PackageFaqOut> => {
  const { data } = await apiClient.post<unknown>(`/packages/${packageId}/faqs`, payload);
  return PackageFaqOutSchema.parse(data);
};

export const updatePackageFaq = async (
  packageId: string,
  childId: string,
  payload: UpdatePackageFaqRequest,
): Promise<PackageFaqOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/faqs/${childId}`,
    payload,
  );
  return PackageFaqOutSchema.parse(data);
};

export const deletePackageFaq = async (packageId: string, childId: string): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/faqs/${childId}`);
};

// ── Images ────────────────────────────────────────────────────────────────────

export const createPackageImage = async (
  packageId: string,
  payload: CreatePackageImageRequest,
): Promise<PackageImageOut> => {
  const { data } = await apiClient.post<unknown>(`/packages/${packageId}/images`, payload);
  return PackageImageOutSchema.parse(data);
};

export const updatePackageImage = async (
  packageId: string,
  childId: string,
  payload: UpdatePackageImageRequest,
): Promise<PackageImageOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/images/${childId}`,
    payload,
  );
  return PackageImageOutSchema.parse(data);
};

export const deletePackageImage = async (packageId: string, childId: string): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/images/${childId}`);
};

// ── Batches & variants ────────────────────────────────────────────────────────

export const createPackageBatch = async (
  packageId: string,
  payload: CreatePackageBatchRequest,
): Promise<PackageBatchOut> => {
  const { data } = await apiClient.post<unknown>(`/packages/${packageId}/batches`, payload);
  return PackageBatchOutSchema.parse(data);
};

export const updatePackageBatch = async (
  packageId: string,
  childId: string,
  payload: UpdatePackageBatchRequest,
): Promise<PackageBatchOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/batches/${childId}`,
    payload,
  );
  return PackageBatchOutSchema.parse(data);
};

export const deletePackageBatch = async (packageId: string, childId: string): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/batches/${childId}`);
};

export const createPackageVariant = async (
  packageId: string,
  batchId: string,
  payload: CreatePackageVariantRequest,
): Promise<PackageVariantOut> => {
  const { data } = await apiClient.post<unknown>(
    `/packages/${packageId}/batches/${batchId}/variants`,
    payload,
  );
  return PackageVariantOutSchema.parse(data);
};

export const updatePackageVariant = async (
  packageId: string,
  batchId: string,
  childId: string,
  payload: UpdatePackageVariantRequest,
): Promise<PackageVariantOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/packages/${packageId}/batches/${batchId}/variants/${childId}`,
    payload,
  );
  return PackageVariantOutSchema.parse(data);
};

export const deletePackageVariant = async (
  packageId: string,
  batchId: string,
  childId: string,
): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/batches/${batchId}/variants/${childId}`);
};

// ── Bulk-set: Inclusions / Exclusions / Collections ───────────────────────────

export const setPackageInclusions = async (
  packageId: string,
  payload: SetPackageInclusionsRequest,
): Promise<PackageInclusionOut[]> => {
  const { data } = await apiClient.put<unknown>(`/packages/${packageId}/inclusions`, payload);
  return z.array(PackageInclusionOutSchema).parse(data);
};

export const setPackageExclusions = async (
  packageId: string,
  payload: SetPackageExclusionsRequest,
): Promise<PackageExclusionOut[]> => {
  const { data } = await apiClient.put<unknown>(`/packages/${packageId}/exclusions`, payload);
  return z.array(PackageExclusionOutSchema).parse(data);
};

export const setPackageCollections = async (
  packageId: string,
  payload: SetPackageCollectionsRequest,
): Promise<PackageCollectionOut[]> => {
  const { data } = await apiClient.put<unknown>(`/packages/${packageId}/collections`, payload);
  return z.array(PackageCollectionOutSchema).parse(data);
};

export const setPackageHeroInclusions = async (
  packageId: string,
  payload: SetPackageHeroInclusionsRequest,
): Promise<PackageHeroInclusionOut[]> => {
  const { data } = await apiClient.put<unknown>(`/packages/${packageId}/hero-inclusions`, payload);
  return z.array(PackageHeroInclusionOutSchema).parse(data);
};
